import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {map} from 'rxjs/operators';
import {Observable, Subject, Subscription} from 'rxjs';
import {faTimes} from '@fortawesome/free-solid-svg-icons/faTimes';
import { MatAutocomplete } from '@angular/material/autocomplete';

@Component({
    selector: 'app-combination-alarm-expression-element',
    templateUrl: './combination-alarm-expression-element.component.html',
    styleUrls: ['./combination-alarm-expression-element.component.css']
})
export class CombinationAlarmExpressionElementComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() readOnly: boolean;
    @Input() element: any;
    @Input() newElementInput: boolean;
    @Input() type: 'operator' | 'alarm';
    @Input() isRequired: boolean;
    @Input() elementsList;
    @Input() isEdited = false;
    @Input() valueExternalChanges: Subject<any>;
    @Input() hasError: boolean;
    @Input() getFocused: Observable<any>;
    @Input() viewIsInited: Observable<any>;
    @Output() updateElement = new EventEmitter();
    @Output() deleteElement = new EventEmitter();
    @Output() focusElement = new EventEmitter();
    @Output() focusOnNextElement = new EventEmitter();
    @ViewChild('auto') autocomplete: MatAutocomplete;
    @ViewChild('editionTemplateInput') input: ElementRef;
    autocompleteFormControl = new FormControl();
    userFilteredElementsObs: Observable<any>;
    faTimes = faTimes;
    isEditedSubsc: Subscription;
    getFocusedSubsc: Subscription;
    viewIsInitedSubsc: Subscription;
    valueAlreadySubmitted: boolean;

    constructor() {
    }

    ngOnInit() {
        this.userFilteredElementsObs = this.autocompleteFormControl.valueChanges.pipe(
            map((userInput) => this.filterElementsByUserInput(userInput))
        );
        if (this.valueExternalChanges) {
            this.isEditedSubsc = this.valueExternalChanges
                .subscribe(
                    (value) => {
                        if (!value && this.isEdited) {
                            this.addElementByKeyboardInput();
                        } else {
                            this.isEdited = false;
                        }
                    }
                );
        }
        if (this.getFocused) {
            this.getFocusedSubsc = this.getFocused.subscribe(
                () => {
                    if (this.input) {
                        this.input.nativeElement.focus();
                    }
                }
            );
        }
        if (this.viewIsInited) {
            this.viewIsInitedSubsc = this.viewIsInited.subscribe(
                () => this.input.nativeElement.focus()
            );
        }
    }

    ngAfterViewInit() {
        if (this.newElementInput) {
            this.input.nativeElement.focus();
        }
    }

    ngOnDestroy() {
        if (this.isEditedSubsc) {
            this.isEditedSubsc.unsubscribe();
        }
        if (this.getFocusedSubsc) {
            this.getFocusedSubsc.unsubscribe();
        }
        if (this.viewIsInitedSubsc) {
            this.viewIsInitedSubsc.unsubscribe();
        }
    }

    filterElementsByUserInput(userInput): any[] {
        if (!userInput) {
            return this.elementsList;
        }
        return this.elementsList.filter(
            (element) => {
                if ('string' !== typeof element.name) {
                    return;
                }
                return element.name.toLowerCase().includes(userInput);
            }
        );
    }

    addElementByOptionSelection(event) {
        if (!event || !event.option) {
            return;
        }
        if (this.newElementInput) {
            // to prevent double event callbacks (value selection also triggers input's keyup.enter event)
            this.valueAlreadySubmitted = true;
        }
        this.emitElementUpdate(event.option.value);
    }

    addElementByKeyboardInput() {
        // case where two alarms have same name ?
        if (this.newElementInput && !this.autocompleteFormControl.value) {
            return;
        }
        if (this.valueAlreadySubmitted) {
            this.valueAlreadySubmitted = false;
            return;
        }
        if (!this.autocompleteFormControl.value) {
            this.emitElementUpdate(undefined);
            return;
        }
        let newElement = this.elementsList.find(
            (element) => {
                if ('string' !== typeof element.name) {
                    return;
                }
                return element.name.toLowerCase() === this.autocompleteFormControl.value.toLowerCase();
            }
        );
        if (!newElement) {
            newElement = this.autocompleteFormControl.value;
        }
        this.emitElementUpdate(newElement);
        this.autocompleteFormControl.setValue('');
    }

    addElementByFocusLoss() {
        // timeout to give 'priority' to click events leading to a 'value external changes'
        setTimeout(
            () => {
                if (this.isEdited
                    || (this.newElementInput && !this.valueAlreadySubmitted && this.autocompleteFormControl.value)) {
                    this.addElementByKeyboardInput();
                }
            }, 200
        );
    }

    setElementAsEditable() {
        if (!this.readOnly) {
            this.isEdited = true;
            this.autocompleteFormControl.setValue('');
            this.focusElement.emit(true);
        }
    }

    private emitElementUpdate(updatedElement) {
        this.updateElement.emit(updatedElement);
        this.isEdited = false;
        // timeout to give 'priority' to click events leading to a 'value external changes'
        setTimeout(
            () => this.focusOnNextElement.emit(true), 500
        );
    }

}
